import React, { useState } from 'react';
import {
  Box, Table, TableHead, TableBody, TableRow, TableCell, Typography, Button,
  Tooltip, CircularProgress, Paper, TextField, InputAdornment,
  Alert, Chip,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  Search, PersonRemove, PersonAdd, Email,
  VerifiedUser,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { lookupUserByEmail, toggleSuperAdmin } from '../../services/tenants';

const RevokeButton = ({ user, onChanged }) => {
  const [busy, setBusy] = useState(false);

  const revoke = async () => {
    setBusy(true);
    try {
      await toggleSuperAdmin(user.id, false);
      toast.success(`${user.name} não é mais super-admin.`, { position: toast.POSITION.BOTTOM_CENTER });
      onChanged?.();
    } catch (error) {
      // A API retorna 422 ao tentar remover o último super-admin da
      // plataforma -- a mensagem do backend é a fonte da verdade, exibida
      // como veio (ver docblock de SuperAdminUserController::toggle).
      const msg = error?.response?.data?.error || 'Não foi possível revogar o super-admin.';
      toast.error(msg, { position: toast.POSITION.BOTTOM_CENTER, autoClose: 6000 });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Tooltip title="Revogar super-admin">
      <span>
        <Button
          size="small"
          variant="outlined"
          color="error"
          disabled={busy}
          onClick={revoke}
          startIcon={busy ? <CircularProgress size={14} color="inherit" /> : <PersonRemove />}
          sx={{ textTransform: 'none', borderRadius: 2 }}
        >
          Revogar
        </Button>
      </span>
    </Tooltip>
  );
};

// Concede super-admin. Busca cross-tenant por e-mail (match exato) --
// GET /users/lookup no backend, atrás do middleware `super.admin`, acha o
// usuário em QUALQUER tenant da plataforma (ver services/tenants.js).
const GrantByEmail = ({ onChanged }) => {
  const theme = useTheme();
  const [email, setEmail] = useState('');
  const [searching, setSearching] = useState(false);
  const [found, setFound] = useState(null);
  const [searched, setSearched] = useState(false);
  const [granting, setGranting] = useState(false);

  const search = async () => {
    const term = email.trim();
    if (!term) return;
    setSearching(true);
    setSearched(false);
    setFound(null);
    try {
      const result = await lookupUserByEmail(term);
      setFound(result);
      setSearched(true);
    } catch (error) {
      // 404 (não encontrado) já é tratado dentro de lookupUserByEmail --
      // aqui só chegam erros de verdade (422 e-mail inválido, 403 sem
      // permissão, rede/500). A mensagem do backend é a fonte da verdade
      // quando existir.
      const msg = error?.response?.data?.error || 'Não foi possível buscar o usuário.';
      toast.error(msg, { position: toast.POSITION.BOTTOM_CENTER });
    } finally {
      setSearching(false);
    }
  };

  const grantFound = async () => {
    if (!found) return;
    setGranting(true);
    try {
      await toggleSuperAdmin(found.id, true);
      toast.success(`${found.name} agora é super-admin.`, { position: toast.POSITION.BOTTOM_CENTER });
      setEmail('');
      setFound(null);
      setSearched(false);
      onChanged?.();
    } catch (error) {
      const msg = error?.response?.data?.error || 'Não foi possível conceder super-admin.';
      toast.error(msg, { position: toast.POSITION.BOTTOM_CENTER });
    } finally {
      setGranting(false);
    }
  };

  return (
    <Paper elevation={0} sx={{ p: 2.5, borderRadius: 3, border: `1px solid ${theme.palette.divider}` }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>Conceder super-admin</Typography>
      <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mb: 2 }}>
        Busque pelo e-mail do usuário, em qualquer empresa (tenant) da plataforma.
      </Typography>

      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'flex-start' }}>
        <TextField
          size="small"
          placeholder="email@exemplo.com"
          value={email}
          onChange={(e) => { setEmail(e.target.value); setSearched(false); setFound(null); }}
          onKeyDown={(e) => { if (e.key === 'Enter') search(); }}
          sx={{ flex: '1 1 260px' }}
          InputProps={{
            startAdornment: <InputAdornment position="start"><Email fontSize="small" /></InputAdornment>,
          }}
        />
        <Button
          variant="outlined"
          onClick={search}
          disabled={searching || !email.trim()}
          startIcon={searching ? <CircularProgress size={14} color="inherit" /> : <Search />}
          sx={{ textTransform: 'none', borderRadius: 2, whiteSpace: 'nowrap' }}
        >
          Buscar
        </Button>
      </Box>

      {searched && found && (
        <Alert
          severity="success"
          sx={{ mt: 2, borderRadius: 2 }}
          action={
            <Button
              size="small"
              color="inherit"
              onClick={grantFound}
              disabled={granting}
              startIcon={granting ? <CircularProgress size={14} color="inherit" /> : <PersonAdd fontSize="small" />}
              sx={{ textTransform: 'none' }}
            >
              Conceder
            </Button>
          }
        >
          {found.name} ({found.email})
        </Alert>
      )}

      {searched && !found && (
        <Alert severity="info" sx={{ mt: 2, borderRadius: 2 }}>
          Nenhum usuário encontrado com esse e-mail.
        </Alert>
      )}
    </Paper>
  );
};

const SuperAdminManager = ({ superAdmins, loading, onChanged }) => {
  const theme = useTheme();

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <GrantByEmail onChanged={onChanged} />

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={26} />
        </Box>
      ) : !superAdmins || superAdmins.length === 0 ? (
        <Paper elevation={0} sx={{ p: 4, borderRadius: 3, border: `1px dashed ${theme.palette.divider}`, textAlign: 'center' }}>
          <VerifiedUser sx={{ fontSize: 32, color: theme.palette.text.secondary, mb: 1 }} />
          <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
            Nenhum super-admin cadastrado.
          </Typography>
        </Paper>
      ) : (
        <Box sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700 }}>Nome</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>E-mail</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Tenant</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="right">Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {superAdmins.map((u) => (
                <TableRow key={u.id} hover>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{u.name}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>{u.email}</Typography>
                  </TableCell>
                  <TableCell>
                    <Chip size="small" variant="outlined" label={`#${u.tenant_id}`} />
                  </TableCell>
                  <TableCell align="right">
                    <RevokeButton user={u} onChanged={onChanged} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      )}
    </Box>
  );
};

export default SuperAdminManager;
